import React from 'react'
import { Box, Chip, Typography } from '@mui/material'
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded'
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded'
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded'
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded'
import { PatientDeclinedReasonV2, PatientHistoryType } from 'src/graphql.generated'
import { DECLINE_REASON_NAMES } from 'src/participant/participantConstants'
import { PATIENT_HISTORY_TYPE_NAMES, PatientHistoryFormType } from 'src/participantHistory/constants'
import {
  CALL_STATUS_OPTIONS,
  shouldShowFullCheckinCallForm,
} from 'src/participantHistoryForm/form/checkinCall/CheckinCallNoteSchema'
import { formatUsDate, formatUsDateTime } from '../../format'
import { advancedColors } from '../../theme'
import { REASON_OPTIONS as MISSED_INTAKE_CALL_REASON_OPTIONS } from './eventLogger/fields/MissedIntakeCallFields'
import { getIn } from './eventLogger/valuePath'
import {
  ADVANCED_FOLLOW_UP_NOTE_SECTIONS,
  INTAKE_NOTE_COACHING_SECTIONS,
  INTAKE_NOTE_COMPREHENSIVE_SECTIONS,
  MONTHLY_SUMMARY_NOTE_SECTIONS,
  TREATMENT_PLAN_COMPREHENSIVE_SECTIONS,
} from './comprehensiveLayouts'
import {
  Field,
  Path,
  Section,
  Value,
  YES_NO,
  checks,
  choice,
  codes,
  date,
  metrics,
  showWhen,
  tags,
  text,
  textsOf,
  value,
  yesno,
} from './historyLayoutKit'
import {
  BURNOUT_OPTIONS,
  EMPLOYMENT_OPTIONS,
  EXPECTED_ACHIEVEMENTS_OPTIONS,
  GENERAL,
  HEALTH_HISTORY_OPTIONS,
  INTERVENTIONS_OPTIONS,
  NEXT_STEPS_OPTIONS,
  Option,
  PARTICIPANT_RESPONSE_OPTIONS,
  PHYSICAL_APPEARANCE_OPTIONS,
  PLAN_OPTIONS,
  PROTECTIVE_FACTORS_OPTIONS,
  PSYCHIATRIC_HISTORY,
  REASON_FOR_CALL_OPTIONS,
  REASON_OPTIONS as DISCHARGE_REASON_OPTIONS,
  SERVICE_DELIVERY_OPTIONS,
  SOCIAL_AND_FUNCTIONAL_HISTORY,
  SUMMARY,
} from './historyOptions'

const WPAI_SECTIONS: Section[] = [
  {
    fields: [
      yesno('Are you currently employed (working for pay)?', 'WPAI_QUESTION_1'),
      value(
        'During the past seven days, how many hours did you miss from work because of problems associated with your depression or burnout?',
        'WPAI_QUESTION_2'
      ),
      value(
        'During the past seven days, how many hours did you miss from work because of any other reason, such as vacation, holidays, time off to participate in this study?',
        'WPAI_QUESTION_3'
      ),
      value('During the past seven days, how many hours did you actually work?', 'WPAI_QUESTION_4'),
      value(
        'During the past seven days, how much did your depression or burnout affect your productivity while you were working?',
        'WPAI_QUESTION_5'
      ),
      value(
        'During the past seven days, how much did your depression or burnout affect your ability to do your regular daily activities, other than work at a job?',
        'WPAI_QUESTION_6'
      ),
      value('Percent work time missed due to health', ['WPAI_SCORE_1', 'SCORE_1']),
      value('Percent impairment while working due to health', ['WPAI_SCORE_2', 'SCORE_2']),
      value('Percent overall work impairment due to health', ['WPAI_SCORE_3', 'SCORE_3']),
      value('Percent activity impairment due to health', ['WPAI_SCORE_4', 'SCORE_4']),
    ],
  },
]

const fullCheckInCall = (entry: Record<string, unknown>) =>
  shouldShowFullCheckinCallForm(typeof entry.CALL_STATUS === 'string' ? entry.CALL_STATUS : undefined)

const CHECK_IN_CALL_SECTIONS: Section[] = [
  {
    fields: [
      value('First name', 'FIRST_NAME'),
      value('Last name', 'LAST_NAME'),
      value('Insurance member ID', 'MEMBER_ID'),
      value('Call start time', 'CALL_START_TIME', 'datetime'),
      value('Call end time', 'CALL_END_TIME', 'datetime'),
      value('Provider name', 'PROVIDER_NAME'),
      value('License type', 'LICENSE_TYPE'),
      choice('Call status', 'CALL_STATUS', CALL_STATUS_OPTIONS),
    ],
  },
  {
    title: 'Service Delivery & Compliance',
    when: fullCheckInCall,
    fields: [checks('', 'SERVICE_DELIVERY_AND_COMPLIANCE', SERVICE_DELIVERY_OPTIONS)],
  },
  {
    title: 'Call Method',
    when: fullCheckInCall,
    fields: [
      choice('', 'CALL_METHOD', [
        { value: 'IN_APP', label: 'In-app' },
        { value: 'PHONE_CALL', label: 'Phone call' },
      ]),
    ],
  },
  { title: 'SMART Goals', when: fullCheckInCall, fields: [text('', 'SMART_GOALS')] },
  { title: 'Billing and Diagnosis Codes', when: fullCheckInCall, fields: [codes('', 'BILLING_CODES')] },
  {
    title: 'Reason for Call',
    when: fullCheckInCall,
    fields: [choice('', 'REASON_FOR_CALL', REASON_FOR_CALL_OPTIONS)],
  },
  {
    title: 'Interventions',
    when: fullCheckInCall,
    fields: [checks('', 'INTERVENTIONS', INTERVENTIONS_OPTIONS, 'Brief summary of call')],
  },
  {
    title: 'Risk Assessment',
    when: fullCheckInCall,
    fields: [
      choice('', 'RISK_LEVEL_HAS_CHANGE', [
        { value: 'no', label: 'No known changes to risk level since last encounter' },
        { value: 'yes', label: 'Changes to risk level' },
      ]),
    ],
  },
  {
    title: 'Protective Factors Identified',
    when: (entry) => fullCheckInCall(entry) && entry.RISK_LEVEL_HAS_CHANGE === 'yes',
    fields: [checks('', 'PROTECTIVE_FACTORS_IDENTIFIED', PROTECTIVE_FACTORS_OPTIONS, 'Other (if applicable)')],
  },
  {
    title: 'Next Steps',
    when: (entry) => fullCheckInCall(entry) && entry.RISK_LEVEL_HAS_CHANGE === 'yes',
    fields: [checks('', 'NEXT_STEPS', NEXT_STEPS_OPTIONS, 'Other (if applicable)')],
  },
  {
    title: 'Participant Response',
    when: fullCheckInCall,
    fields: [checks('', 'PARTICIPANT_RESPONSE', PARTICIPANT_RESPONSE_OPTIONS, 'Other (if applicable)')],
  },
  { title: 'Plan', when: fullCheckInCall, fields: [checks('', 'PLAN', PLAN_OPTIONS, 'Other (if applicable)')] },
  { title: 'Description', when: (entry) => !fullCheckInCall(entry), fields: [text('', 'DESCRIPTION')] },
]

const DECLINED_REASONS_V1: Record<string, string> = {
  NOT_INTERESTED: 'Not Interested',
  NO_PAYER: 'No Payer',
  NOT_RESPONDING: 'Not Responding',
  PREFERS_F2F_THERAPY: 'Prefers F2F Therapy',
  TIME_COMMITMENT: 'Time Commitment',
  OTHER: 'Other',
  ACUTE_TRAUMA_SYMPTOMS: 'Acute Trauma Symptoms',
  NO_SYMPTOMS: 'No Symptoms',
  OTHER_MENTAL_DISORDERS: 'Other Mental Disorders',
  SUICIDALITY: 'Suicidality',
}

const declinedReasonName = (code: string, source: Value) => {
  // eslint-disable-next-line no-underscore-dangle
  const isV1 = source?.__version === 1
  const name = isV1 ? DECLINED_REASONS_V1[code] : DECLINE_REASON_NAMES.get(code as PatientDeclinedReasonV2)

  return name ?? code
}

const ENGAGEMENTS: Option[] = [
  { value: 'completer', label: 'Completer' },
  { value: 'dropout', label: 'Dropout' },
  { value: 'noShow', label: 'No show' },
]

const LAYOUTS: Partial<Record<PatientHistoryType, Section[]>> = {
  [PatientHistoryType.Burnout]: [{ fields: [choice('Burnout Questionnaire', 'BURNOUT_SCORE', BURNOUT_OPTIONS)] }],
  [PatientHistoryType.Wpai]: WPAI_SECTIONS,
  [PatientHistoryType.MonthlySummaryNote]: MONTHLY_SUMMARY_NOTE_SECTIONS,
  [PatientHistoryType.IntakeNoteComprehensive]: INTAKE_NOTE_COMPREHENSIVE_SECTIONS,
  [PatientHistoryType.TreatmentPlanComprehensive]: TREATMENT_PLAN_COMPREHENSIVE_SECTIONS,
  [PatientHistoryType.TreatmentPlanComprehensiveUpdate]: TREATMENT_PLAN_COMPREHENSIVE_SECTIONS,
  [PatientHistoryType.IntakeNoteCoaching]: INTAKE_NOTE_COACHING_SECTIONS,
  [PatientHistoryType.AdvancedFollowUpNote]: ADVANCED_FOLLOW_UP_NOTE_SECTIONS,
  [PatientHistoryType.MissedIntakeCall]: [{ fields: [choice('', 'reason', MISSED_INTAKE_CALL_REASON_OPTIONS)] }],
  [PatientHistoryType.Accepted]: [
    {
      fields: [
        date('Program Start Date', 'programStartDate'),
        value('Source', 'source'),
        value('Program ID', 'programID'),
      ],
    },
  ],
  [PatientHistoryType.Declined]: [
    { fields: [tags('Reasons', 'reasons', declinedReasonName), text('Description', 'description')] },
  ],
  [PatientHistoryType.ProgramEnd]: [
    {
      fields: [
        value('Program ID', 'programID'),
        choice('Engagement', 'engagement', ENGAGEMENTS),
        metrics('', 'programEndMetrics'),
        value('PHQ-9 Baseline', 'baselinePHQ9'),
        value('PHQ-9 Program End', 'programEndPHQ9'),
        value('GAD-7 Baseline', 'baselineGAD7'),
        value('GAD-7 Program End', 'programEndGAD7'),
      ],
    },
  ],
  [PatientHistoryType.CheckInCall]: CHECK_IN_CALL_SECTIONS,
  [PatientHistoryType.TreatmentPlan]: [
    {
      title: 'Treatment Plan',
      fields: [text('Presenting Problem', 'presentingProblem'), text('Treatment Goals', 'treatmentGoals')],
    },
    { title: 'Billing and Diagnosis Codes', fields: [codes('', 'billingCodes')] },
    {
      title: 'Interventions/ Engagement Plan',
      fields: [
        checks('', 'interventionsAndEngagementPlan', [
          {
            key: 'participateTreatmentProgram',
            label:
              'Client will participate in Meru Treatment program including daily lessons, communication with therapist and biofeedback',
          },
          { key: 'followupTelehealthSessions', label: 'Follow up telehealth sessions as needed' },
          {
            key: 'utilizeCommunicationChannels',
            label:
              'Therapist will utilize in app chat to communicate with participant and escalate to SMS, email or phone calls in order to re-engage client in treatment as needed',
          },
          { key: 'participantAgreement', label: 'Participant agrees to the discussed Treatment plan' },
        ]),
        text('Additional Resources Provided (include details)', 'interventionsAndEngagementPlan.additionalResources'),
        text(
          'Coordination of care with external providers (include details)',
          'interventionsAndEngagementPlan.coordinationWithExternalProviders'
        ),
        text('Other', 'interventionsAndEngagementPlan.other'),
      ],
    },
    { title: 'Obstacles and Action Plan', fields: [text('', 'obstaclesAndActionPlan')] },
  ],
  [PatientHistoryType.DischargeSummaryNote]: [
    {
      title: 'Participant',
      fields: [
        value('First Name', 'participant.firstName'),
        value('Last Name', 'participant.lastName'),
        value('Insurance member ID', 'participant.memberID'),
      ],
    },
    {
      title: 'Assessing Practitioner',
      fields: [value('Name', 'assessingPractitioner.name'), value('License Type', 'assessingPractitioner.licenseType')],
    },
    { title: 'Discharge Date', fields: [date('', 'dischargeDate')] },
    { title: 'Billing and Diagnosis Codes', fields: [codes('', 'billingCodes')] },
    {
      title: 'Reason for Discharge',
      fields: [
        choice('', 'reasonForDischarge.reason', DISCHARGE_REASON_OPTIONS),
        text('Details', 'reasonForDischarge.details'),
      ],
    },
    { title: 'SMART Goals', fields: [text('', 'treatmentPlan.smartGoals')] },
    {
      title: 'Goals Met',
      fields: [
        choice('', 'treatmentPlan.goalsMet', YES_NO),
        showWhen((entry) => getIn(entry, 'treatmentPlan.goalsMet') === 'no', text('Details', 'treatmentPlan.details')),
      ],
    },
  ],
  [PatientHistoryType.IntakeNote]: [
    {
      title: 'Identifying information and treatment expectations',
      fields: [
        text('Identifying Information', 'IDENTIFYING_INFO'),
        text('Presenting Problem / Reason for Referral', 'EXPECTATIONS_AND_INTERESTS'),
      ],
    },
    {
      title: 'Clinical Assessment',
      fields: [
        choice('Are you currently…', 'EMPLOYMENT.STATUS', EMPLOYMENT_OPTIONS),
        checks('Health History', 'HEALTH_HISTORY', HEALTH_HISTORY_OPTIONS),
        text('Clinical formulation and timeline of symptoms', 'TIMELINE_OF_SYMPTOMS_AND_TREATMENT'),
        value('Previous MDD episodes', 'MAJOR_DEPRESSION_EPISODES'),
        text('Suicide Risk Assessment', 'SUICIDE_RISK'),
        codes('Billing code and diagnosis', 'BILLING_CODES'),
      ],
    },
    {
      title: 'Treatment plan',
      fields: [
        checks(
          'What would you like to have more of in your life?',
          'EXPECTED_ACHIEVEMENTS',
          EXPECTED_ACHIEVEMENTS_OPTIONS
        ),
        text('Reason for selecting other', 'EXPECTED_ACHIEVEMENTS.OTHER_REASON'),
        text('Treatment goals', 'TREATMENT_GOALS'),
        text('Obstacles and Action Plan', 'OBSTACLES_AND_ACTION_PLAN'),
        text('Internal and External stressors', 'INTERNAL_EXTERNAL_STRESSORS'),
        text('Is there something that you want your therapist to know?', 'OBSTACLES'),
      ],
    },
    {
      title: 'Confirmation and Next steps',
      fields: [
        yesno('The participant is not eligible for the program', 'PARTICIPANT_INELIGIBLE'),
        yesno('The participant is eligible for the program', 'STARTING_DATE_CONFIRMED'),
        yesno('Reviewed confidentiality and exceptions', 'CONSENT.REVIEWED_CONFIDENTIALITY_AND_EXCEPTIONS'),
        yesno('Therapist obtained verbal consent for treatment', 'CONSENT.VERBAL_CONSENT_FOR_TREATMENT'),
        yesno('Create quick note', 'CREATE_QUICK_NOTE'),
      ],
    },
  ],
  [PatientHistoryType.IntakeNotePsychiatry]: [
    {
      title: 'General intake information',
      fields: [
        yesno('Visit Type: Synchronous audio-video', 'visitTypeRemote'),
        ...GENERAL.map(([key, label]) => showWhen((source) => Boolean(getIn(source, key)), text(label, key))),
        showWhen(
          (source) => Boolean(getIn(source, 'physicalAppearance')),
          choice('Physical appearance', 'physicalAppearance', PHYSICAL_APPEARANCE_OPTIONS)
        ),
        text('Note', 'hpi'),
      ],
    },
    {
      title: 'Psychiatric History',
      when: (source) => PSYCHIATRIC_HISTORY.some(([key]) => Boolean(getIn(source, `psychiatricHistory.${key}`))),
      fields: textsOf(PSYCHIATRIC_HISTORY, 'psychiatricHistory.'),
    },
    {
      title: 'Social and Functional History',
      when: (source) =>
        SOCIAL_AND_FUNCTIONAL_HISTORY.some(([key]) => Boolean(getIn(source, `socialAndFunctionalHistory.${key}`))),
      fields: textsOf(SOCIAL_AND_FUNCTIONAL_HISTORY, 'socialAndFunctionalHistory.'),
    },
    {
      title: 'Summary and Treatment Plan',
      when: (source) => SUMMARY.some(([key]) => Boolean(getIn(source, key))),
      fields: textsOf(SUMMARY),
    },
    { title: 'Billing code and diagnosis', fields: [codes('', 'BILLING_CODES')] },
  ],
}

export const FINANCIAL_ELIGIBILITY_LAYOUT: Section[] = [
  {
    title: 'Cost estimate',
    fields: [
      value('Intake cost estimate', 'intake'),
      value('Month 1 cost estimate', 'month1'),
      value('Month 2 cost estimate', 'month2'),
      value('Month 3 cost estimate', 'month3'),
    ],
  },
  { fields: [text('Description', 'description')] },
]

export const VIDEO_CALL_LAYOUT: Section[] = [
  {
    fields: [
      value('Participants', 'participants'),
      value('Call Duration', 'duration'),
      value('Reason', 'reason'),
      text('Call notes', 'notes'),
    ],
  },
]

export const NUTRITION_INTAKE_LAYOUT: Section[] = [{ fields: [text('Nutrition intake notes', 'description')] }]

export const layoutForType = (type: PatientHistoryType | null | undefined): Section[] | undefined =>
  type ? LAYOUTS[type] : undefined

const readOne = (source: Value, path: string): unknown =>
  path
    .split('.')
    .reduce<unknown>(
      (current, key) =>
        typeof current === 'object' && current !== null ? (current as Record<string, unknown>)[key] : undefined,
      source
    )

const read = (source: Value, path: Path): unknown =>
  (Array.isArray(path) ? path : [path]).map((candidate) => readOne(source, candidate)).find((found) => found != null)

// Entries logged before the date-time input stored a bare `HH:mm`, which has no day to format.
const shown = (field: Field, stored: string) =>
  field.kind === 'value' && field.input === 'datetime' && /^\d{4}-\d{2}-\d{2}T/.test(stored)
    ? formatUsDateTime(stored)
    : stored

const yesNoLabel = (flag: boolean) => (flag ? 'Yes' : 'No')

const isBlank = (candidate: unknown) => candidate === undefined || candidate === null || candidate === ''

const Muted = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
    {children}
  </Typography>
)

const OptionRow = ({ label, selected, multiple }: { label: string; selected: boolean; multiple: boolean }) => {
  const checked = multiple ? CheckBoxRoundedIcon : RadioButtonCheckedRoundedIcon
  const unchecked = multiple ? CheckBoxOutlineBlankRoundedIcon : RadioButtonUncheckedRoundedIcon
  const Icon = selected ? checked : unchecked

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Icon
        fontSize="small"
        sx={{ color: selected ? advancedColors.green : advancedColors.line, flexShrink: 0, mt: '1px' }}
      />
      <Typography
        variant="body2"
        sx={{ color: selected ? advancedColors.ink : advancedColors.inkMuted, fontWeight: selected ? 600 : 400 }}
      >
        {label}
      </Typography>
    </Box>
  )
}

const FieldValue = ({ field, source }: { field: Field; source: Value }) => {
  const stored = read(source, field.path)

  switch (field.kind) {
    case 'choice': {
      const picked = isBlank(stored) ? undefined : String(stored)
      const known = field.options.some((option) => option.value === picked)

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {field.options.map((option) => (
            <OptionRow key={option.value} label={option.label} selected={option.value === picked} multiple={false} />
          ))}
          {picked !== undefined && !known && <OptionRow label={picked} selected multiple={false} />}
        </Box>
      )
    }
    case 'checks': {
      const group = typeof stored === 'object' && stored !== null ? (stored as Record<string, unknown>) : undefined
      const other = group && typeof group.OTHER === 'string' && group.OTHER.trim() ? group.OTHER : undefined

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {field.options.map((option) => (
            <OptionRow key={option.key} label={option.label} selected={Boolean(group?.[option.key])} multiple />
          ))}
          {field.otherLabel && other && <OptionRow label={`${field.otherLabel}: ${other}`} selected multiple />}
        </Box>
      )
    }
    case 'tags':
      return Array.isArray(stored) && stored.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {stored.map((code) => (
            <Chip key={String(code)} size="small" label={field.nameFor(String(code), source)} />
          ))}
        </Box>
      ) : (
        <Muted>—</Muted>
      )
    case 'metrics': {
      const entries = typeof stored === 'object' && stored !== null ? Object.entries(stored) : []

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {entries.flatMap(([metric, scores]) => {
            const name = PATIENT_HISTORY_TYPE_NAMES[metric as PatientHistoryFormType] ?? metric
            const { baseline, programEnd } = (scores ?? {}) as Record<string, unknown>

            return [
              <Box key={`${metric}:baseline`}>
                <Typography
                  variant="body2"
                  sx={{ color: advancedColors.inkMuted, mb: 0.5 }}
                >{`${name} Baseline`}</Typography>
                <Typography variant="body2">{isBlank(baseline) ? '—' : String(baseline)}</Typography>
              </Box>,
              <Box key={`${metric}:programEnd`}>
                <Typography
                  variant="body2"
                  sx={{ color: advancedColors.inkMuted, mb: 0.5 }}
                >{`${name} Program End`}</Typography>
                <Typography variant="body2">{isBlank(programEnd) ? '—' : String(programEnd)}</Typography>
              </Box>,
            ]
          })}
        </Box>
      )
    }
    case 'rows': {
      const items = Array.isArray(stored) ? stored : []

      return items.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item, index) => (
            <Typography
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              variant="body2"
            >
              {field.columns
                .map(({ key, label }) => `${label}: ${String((item as Record<string, unknown>)?.[key] ?? '—')}`)
                .join(' · ')}
            </Typography>
          ))}
        </Box>
      ) : (
        <Muted>—</Muted>
      )
    }
    case 'codes':
      return Array.isArray(stored) && stored.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {stored.map((code) => (
            <Chip key={String(code)} size="small" label={String(code)} />
          ))}
        </Box>
      ) : (
        <Muted>—</Muted>
      )
    case 'yesno':
      return <Typography variant="body2">{typeof stored === 'boolean' ? yesNoLabel(stored) : '—'}</Typography>
    case 'date':
      return <Typography variant="body2">{typeof stored === 'string' ? formatUsDate(stored) : '—'}</Typography>
    case 'text':
      return isBlank(stored) ? (
        <Muted>—</Muted>
      ) : (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {String(stored)}
        </Typography>
      )
    default:
      return <Typography variant="body2">{isBlank(stored) ? '—' : shown(field, String(stored))}</Typography>
  }
}

const wasRecorded = (field: Field, source: Value) => read(source, field.path) !== undefined

export const LayoutBody = ({ sections, source }: { sections: Section[]; source: Value }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
    {sections.map((section, index) => {
      if (section.when?.(source ?? {}) === false) {
        return null
      }

      const fields = section.fields.filter(
        (field) => wasRecorded(field, source) && field.when?.(source ?? {}) !== false
      )

      if (fields.length === 0) {
        return null
      }

      return (
        <Box key={section.title ?? index} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {section.title && <Typography variant="overline">{section.title}</Typography>}
          {fields.map((field) => (
            <Box key={`${field.label}:${String(field.path)}`} sx={{ minWidth: 0 }}>
              {field.label && (
                <Typography variant="body2" sx={{ color: advancedColors.inkMuted, mb: 0.5 }}>
                  {field.label}
                </Typography>
              )}
              <FieldValue field={field} source={source} />
            </Box>
          ))}
        </Box>
      )
    })}
  </Box>
)
